import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Sunrise, RefreshCw, Undo2, X, CheckCircle2, AlertTriangle, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Operational admin screen -> English (operational-UI language boundary)
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const ReintroductionWeekCard = () => {
  const [open, setOpen] = useState(false);
  const [params, setParams] = useState({
    start_date: todayStr(),
    load_reduction_pct: 20,
    set_reduction: 1,
    rpe_cap: 7,
    max_sessions_per_client: 5,
    add_client_note: false,
  });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null); // { batch_id, applied_workouts, ... }
  const [error, setError] = useState(null);
  const [lastApplied, setLastApplied] = useState(undefined); // undefined=loading, null=none

  const loadLastApplied = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/reintro-week/last-applied`);
      setLastApplied(res.data?.last_applied_at || null);
    } catch {
      setLastApplied(null);
    }
  }, []);

  useEffect(() => { loadLastApplied(); }, [loadLastApplied]);

  const upd = (k, v) => { setParams((p) => ({ ...p, [k]: v })); setPreview(null); setResult(null); };

  const runPreview = async () => {
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await axios.post(`${API}/admin/reintro-week/preview`, params);
      setPreview(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const runApply = async () => {
    setBusy(true); setError(null);
    try {
      const res = await axios.post(`${API}/admin/reintro-week/apply`, params);
      setResult(res.data);
      setPreview(null);
      loadLastApplied();
    } catch (e) {
      setError(e.response?.data?.detail || 'Apply failed');
    } finally { setBusy(false); }
  };

  const runRevert = async () => {
    if (!result?.batch_id) return;
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/admin/reintro-week/revert`, { batch_id: result.batch_id });
      setResult({ ...result, reverted: true });
      loadLastApplied();
    } catch (e) {
      setError(e.response?.data?.detail || 'Revert failed');
    } finally { setBusy(false); }
  };

  const close = () => { setOpen(false); setPreview(null); setResult(null); setError(null); };

  return (
    <>
      <div className="glass-card p-5 border border-amber-400/30" data-testid="reintro-week-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-amber-500/20">
              <Sunrise className="w-6 h-6 text-amber-300" />
            </div>
            <div>
              <h3 className="text-base md:text-lg font-semibold text-white">Reintroduction Week</h3>
              <p className="text-blue-300/80 text-sm mt-0.5 max-w-xl">
                Returning after a break/closure? Soften everyone's upcoming week — lighter loads,
                one fewer set, an RPE cap — so nobody jumps back to top weights cold. Reversible.
              </p>
              <p className="text-xs text-amber-300/70 mt-1.5" data-testid="reintro-last-applied">
                {lastApplied === undefined
                  ? 'Checking history…'
                  : lastApplied
                    ? `Last reintroduction applied: ${fmtDate(lastApplied)}`
                    : 'No previous reintroduction'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setOpen(true)}
            className="text-sm font-semibold px-4 py-2 rounded-lg bg-amber-500 text-black hover:bg-amber-400 transition whitespace-nowrap"
            data-testid="reintro-open-btn"
          >
            Plan reintroduction week
          </button>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 p-4" data-testid="reintro-modal">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90dvh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-slate-700 sticky top-0 bg-slate-900 z-10">
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                <Sunrise className="w-5 h-5 text-amber-300" /> Reintroduction Week
              </h3>
              <button onClick={close} data-testid="reintro-close-btn" className="text-slate-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* First-session guidance */}
              <div className="flex gap-2 text-xs text-blue-200/90 bg-blue-500/10 border border-blue-500/25 rounded-lg p-3" data-testid="reintro-guidance">
                <Info className="w-4 h-4 shrink-0 text-blue-300 mt-0.5" />
                <p>
                  <span className="font-semibold text-blue-100">RPE {params.rpe_cap} is a maximum, not a target.</span>{' '}
                  The first sessions back may feel closer to RPE 5–6 — that's expected. The tool starts
                  everyone ~{params.load_reduction_pct}% lighter and leaves margin to readapt across the week;
                  it never pushes load up to reach the cap. Adjust individual clients manually if needed.
                </p>
              </div>

              {/* Settings */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <label className="text-sm text-blue-200">
                  Start date
                  <input type="date" value={params.start_date}
                    onChange={(e) => upd('start_date', e.target.value)}
                    className="input-field mt-1" data-testid="reintro-start-date" />
                </label>
                <label className="text-sm text-blue-200">
                  Load reduction %
                  <input type="number" min="0" max="60" value={params.load_reduction_pct}
                    onChange={(e) => upd('load_reduction_pct', Number(e.target.value))}
                    className="input-field mt-1" data-testid="reintro-load-pct" />
                </label>
                <label className="text-sm text-blue-200">
                  Sets removed
                  <input type="number" min="0" max="3" value={params.set_reduction}
                    onChange={(e) => upd('set_reduction', Number(e.target.value))}
                    className="input-field mt-1" data-testid="reintro-set-reduction" />
                </label>
                <label className="text-sm text-blue-200">
                  RPE cap
                  <input type="number" min="5" max="10" value={params.rpe_cap}
                    onChange={(e) => upd('rpe_cap', Number(e.target.value))}
                    className="input-field mt-1" data-testid="reintro-rpe-cap" />
                </label>
                <label className="text-sm text-blue-200">
                  Max sessions / client
                  <input type="number" min="1" max="7" value={params.max_sessions_per_client}
                    onChange={(e) => upd('max_sessions_per_client', Number(e.target.value))}
                    className="input-field mt-1" data-testid="reintro-max-sessions" />
                </label>
              </div>

              {/* Optional client note */}
              <label className="flex items-start gap-2 text-sm text-blue-200 cursor-pointer" data-testid="reintro-note-toggle">
                <input type="checkbox" checked={params.add_client_note}
                  onChange={(e) => upd('add_client_note', e.target.checked)}
                  className="mt-0.5" data-testid="reintro-note-checkbox" />
                <span>
                  Show a note to each client on their first session back
                  <span className="block text-xs text-blue-300/60 italic mt-0.5">
                    "Semana de readaptación — volvemos progresivamente tras el cierre."
                  </span>
                </span>
              </label>

              <p className="text-xs text-blue-300/60">
                Applies to all active clients' upcoming sessions in the 7 days from the start date
                (cardio is left unchanged). Weights round to the nearest 0.5&nbsp;kg. Workouts outside
                this window, templates, completed sessions and genuine deloads are never touched.
              </p>

              {error && (
                <div className="flex items-center gap-2 text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg p-3" data-testid="reintro-error">
                  <AlertTriangle className="w-4 h-4" /> {error}
                </div>
              )}

              {/* Preview result */}
              {preview && (
                <div data-testid="reintro-preview">
                  {/* Aggregate summary */}
                  <div className="bg-slate-800/60 rounded-xl p-4 mb-3 border border-amber-400/20" data-testid="reintro-summary">
                    <div className="text-white font-semibold mb-2">
                      {fmtDate(preview.start_date)} → {fmtDate(preview.end_date)}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1.5 text-sm">
                      <div><span className="text-white font-bold">{preview.client_count}</span> <span className="text-blue-300/70">clients</span></div>
                      <div><span className="text-white font-bold">{preview.workout_count}</span> <span className="text-blue-300/70">sessions</span></div>
                      <div><span className="text-white font-bold">{preview.strength_exercises_modified}</span> <span className="text-blue-300/70">strength ex. modified</span></div>
                      <div><span className="text-white font-bold">{preview.cardio_exercises_unchanged}</span> <span className="text-blue-300/70">cardio ex. unchanged</span></div>
                      <div><span className="text-amber-300 font-bold">−{preview.avg_load_reduction_pct}%</span> <span className="text-blue-300/70">load</span></div>
                      <div><span className="text-amber-300 font-bold">−{preview.set_reduction}</span> <span className="text-blue-300/70">set · RPE cap {preview.rpe_cap}</span></div>
                    </div>
                  </div>

                  {preview.client_count === 0 ? (
                    <p className="text-blue-300/70 text-sm py-3">No active clients have upcoming sessions in this window.</p>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {preview.clients.map((c) => (
                        <div key={c.client_id} className="bg-slate-800/50 rounded-lg p-3 text-sm" data-testid={`reintro-preview-client-${c.client_id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-white font-medium">{c.client_name}</span>
                            <span className={`text-xs ${c.sessions >= 4 ? 'text-amber-300 font-semibold' : 'text-blue-300/70'}`}>
                              {c.sessions} session{c.sessions === 1 ? '' : 's'} · {fmtDate(preview.start_date)}–{fmtDate(preview.end_date)}
                            </span>
                          </div>
                          {c.sample && (
                            <div className="mt-1 text-xs text-blue-200/80">
                              {c.sample.exercise}: <span className="text-slate-400 line-through">{c.sample.before}</span> → <span className="text-amber-200">{c.sample.after}</span>
                            </div>
                          )}
                          {c.already_applied && <div className="text-[11px] text-amber-300/80 mt-0.5">Already has a reintro applied — will be skipped.</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Apply result */}
              {result && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4" data-testid="reintro-result">
                  {result.reverted ? (
                    <p className="text-blue-200 text-sm flex items-center gap-2"><Undo2 className="w-4 h-4" /> Reverted — all originals restored.</p>
                  ) : (
                    <>
                      <p className="text-green-200 text-sm flex items-center gap-2 font-medium">
                        <CheckCircle2 className="w-4 h-4" /> Applied to {result.applied_workouts} session(s) across {result.clients_affected} client(s).
                        {result.skipped_already_applied > 0 && <span className="text-amber-300/80"> ({result.skipped_already_applied} skipped)</span>}
                      </p>
                      <p className="text-blue-300/70 text-xs mt-1">
                        {result.strength_exercises_modified} strength exercises modified · {result.cardio_exercises_unchanged} cardio unchanged
                      </p>
                      <button onClick={runRevert} disabled={busy}
                        className="mt-3 text-sm font-semibold px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50 inline-flex items-center gap-2"
                        data-testid="reintro-revert-btn">
                        <Undo2 className="w-4 h-4" /> Undo this batch
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Footer actions */}
            {!result && (
              <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-700 sticky bottom-0 bg-slate-900">
                <button onClick={runPreview} disabled={busy}
                  className="text-sm font-semibold px-4 py-2 rounded-lg bg-slate-700 text-white hover:bg-slate-600 disabled:opacity-50 inline-flex items-center gap-2"
                  data-testid="reintro-preview-btn">
                  {busy && !preview ? <RefreshCw className="w-4 h-4 animate-spin" /> : null} Preview
                </button>
                <button onClick={runApply}
                  disabled={busy || !preview || preview.client_count === 0}
                  className="text-sm font-semibold px-4 py-2 rounded-lg bg-amber-500 text-black hover:bg-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="reintro-apply-btn">
                  {preview ? `Apply to ${preview.client_count} client(s)` : 'Preview first'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReintroductionWeekCard;
